<template>
  <v-navigation-drawer
    v-model="open"
    app
    right
    temporary
    width="360"
    class="lucy-panel"
    @keydown.esc.native="open = false"
  >
    <div class="lucy-panel__inner">
      <header class="lucy-panel__head">
        <span class="lucy-panel__avatar">L</span>
        <div class="flex-grow-1">
          <div class="lucy-panel__name">{{ $t('lucy.name') }}</div>
          <div class="caption text--secondary">{{ subtitle }}</div>
        </div>
        <v-btn icon small :aria-label="$t('close')" @click="open = false">
          <v-icon small>close</v-icon>
        </v-btn>
      </header>

      <p class="lucy-panel__hello">{{ hello }}</p>

      <div v-if="answer" class="lucy-panel__answer mb-4">
        <div class="font-weight-bold mb-1">{{ answerTitle }}</div>
        <p class="mb-3">{{ answer }}</p>
        <v-btn
          v-if="answerMetric"
          small
          depressed
          class="text-none"
          color="ai"
          dark
          @click="go(answerMetric)"
        >
          {{ $t('lucy.goThere') }}
        </v-btn>
      </div>

      <div class="caption font-weight-bold text--secondary mb-2">{{ $t('lucy.presetsLabel') }}</div>
      <button
        v-for="item in presets"
        :key="item.id"
        type="button"
        class="lucy-panel__preset"
        @click="ask(item.id)"
      >
        {{ item.label }}
      </button>

      <p class="caption text--secondary mt-4 mb-0">{{ $t('lucy.phaseNote') }}</p>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {SeoScoreMetricName, seoScoreMetricNames, SeoScoreStatus} from '@/model/SeoScore';
    import SeoScoreMetricsMixin from '@/mixins/SeoScoreMetricsMixin';
    import {EventBus} from '@/utils/EventBus';
    import {firstAiVisibilityGap, lucyAdviceForMetric, lucyCiteAdvice, lucyMetricIsDone, LucyPanelPayload, LucyQuestionId} from '@/service/lucyCoach';
    import {focusSeoMetric} from '@/service/seoScoreFocus';
    import EventsListenerMixin from '@/mixins/EventsListenerMixin';

    type LucyPanelVm = Vue & {
        getStatus(name: SeoScoreMetricName): SeoScoreStatus;
        stats: Record<SeoScoreStatus, number>;
        seoScore: number;
    };

    export default Vue.extend({
        name: 'LucyPanel',
        mixins: [SeoScoreMetricsMixin, EventsListenerMixin],
        data: () => ({
            open: false,
            answer: '',
            answerTitle: '',
            answerMetric: null as SeoScoreMetricName | null,
        }),
        computed: {
            events(): Record<string, (payload?: LucyPanelPayload) => void> {
                return {
                    'lucy-panel-open': this.onOpen,
                    'lucy-panel-close': this.onClose,
                };
            },
            subtitle(): string {
                if (!this.$store.getters.article.id) {
                    return String(this.$t('lucy.noArticle'));
                }
                return String(this.$t('lucy.contextLine', [
                    this.seoScore,
                    (this as unknown as LucyPanelVm).stats.RED
                ]));
            },
            hello(): string {
                if (!this.$store.getters.article.id) {
                    return String(this.$t('lucy.helloEmpty'));
                }
                const next = this.nextMetric;
                if (!next) {
                    return String(this.$t('lucy.helloDone', [this.seoScore]));
                }
                return String(this.$t('lucy.helloWork', [
                    this.seoScore,
                    this.$t('seoScore.' + next + '.label')
                ]));
            },
            nextMetric(): SeoScoreMetricName | null {
                if (!this.$store.getters.article.id) {
                    return null;
                }
                const vm = this as unknown as LucyPanelVm;
                const article = this.$store.getters.article;
                if (!lucyMetricIsDone('aiVisibility', vm.getStatus('aiVisibility'), article)) {
                    return firstAiVisibilityGap(article) || 'faqPresent';
                }
                const red = seoScoreMetricNames.find((name) => {
                    return name !== 'aiVisibility'
                        && vm.getStatus(name) === 'RED'
                        && !lucyMetricIsDone(name, vm.getStatus(name), article);
                });
                if (red) {
                    return red;
                }
                return seoScoreMetricNames.find((name) => {
                    return vm.getStatus(name) === 'YELLOW' && !lucyMetricIsDone(name, vm.getStatus(name), article);
                }) || null;
            },
            presets(): Array<{ id: LucyQuestionId; label: string }> {
                return [
                    {id: 'next', label: String(this.$t('lucy.presets.next'))},
                    {id: 'aiVisibility', label: String(this.$t('lucy.presets.aiVisibility'))},
                    {id: 'cite', label: String(this.$t('lucy.presets.cite'))},
                ];
            }
        },
        methods: {
            onOpen(payload: LucyPanelPayload = {}): void {
                this.open = true;
                if (payload.metric) {
                    this.explainMetric(payload.metric);
                    return;
                }
                this.ask(payload.question || 'next');
            },
            onClose(): void {
                this.open = false;
            },
            ask(id: LucyQuestionId): void {
                if (!this.$store.getters.article.id) {
                    this.answerTitle = String(this.$t('lucy.ask'));
                    this.answer = String(this.$t('lucy.helloEmpty'));
                    this.answerMetric = null;
                    return;
                }
                if (id === 'aiVisibility') {
                    this.explainMetric('aiVisibility');
                    return;
                }
                if (id === 'cite') {
                    const advice = lucyCiteAdvice(
                        this.$store.getters.article,
                        (key, values) => this.$t(key, values as never)
                    );
                    this.answerTitle = String(this.$t('lucy.presets.cite'));
                    this.answer = advice.body;
                    this.answerMetric = advice.goMetric;
                    return;
                }
                if (id === 'redList') {
                    this.listReds();
                    return;
                }
                const next = this.nextMetric;
                if (!next) {
                    this.answerTitle = String(this.$t('lucy.presets.next'));
                    this.answer = String(this.$t('lucy.helloDone', [this.seoScore]));
                    this.answerMetric = null;
                    return;
                }
                this.explainMetric(next);
            },
            listReds(): void {
                const vm = this as unknown as LucyPanelVm;
                const article = this.$store.getters.article;
                const reds = seoScoreMetricNames
                    .filter((name) => vm.getStatus(name) === 'RED' && !lucyMetricIsDone(name, vm.getStatus(name), article))
                    .map((name) => this.$t('seoScore.' + name + '.label'));
                this.answerTitle = String(this.$t('lucy.presets.redList'));
                this.answer = reds.length
                    ? String(this.$t('lucy.redListBody', [reds.slice(0, 6).join(', ')]))
                    : String(this.$t('lucy.noReds'));
                this.answerMetric = this.nextMetric;
            },
            explainMetric(metric: SeoScoreMetricName): void {
                const vm = this as unknown as LucyPanelVm;
                const advice = lucyAdviceForMetric(
                    metric,
                    vm.getStatus(metric),
                    this.$store.getters.article,
                    (key, values) => this.$t(key, values as never),
                    (key) => this.$te(key)
                );
                this.answerTitle = String(this.$t('seoScore.' + metric + '.label'));
                this.answer = advice.body;
                this.answerMetric = advice.goMetric;
            },
            go(metric: SeoScoreMetricName): void {
                this.open = false;
                if (this.$route.name !== 'Article' && this.$store.getters.article.id) {
                    this.$router.push({
                        path: '/article/' + this.$store.getters.article.id,
                        query: {seoFocus: metric}
                    }).catch(() => undefined);
                    return;
                }
                focusSeoMetric(metric);
            }
        }
    });
</script>

<style scoped lang="sass">
.lucy-panel__inner
    padding: 16px 16px 24px

.lucy-panel__head
    display: flex
    align-items: center
    gap: 10px
    margin-bottom: 14px

.lucy-panel__avatar
    width: 36px
    height: 36px
    border-radius: 50%
    background: linear-gradient(135deg, #cd7ec2, #8e3d82)
    color: #fff
    font-weight: 800
    display: inline-flex
    align-items: center
    justify-content: center

.lucy-panel__name
    font-weight: 800
    font-size: 15px

.lucy-panel__hello
    font-size: 14px
    line-height: 1.45
    margin-bottom: 16px

.lucy-panel__answer
    background: rgba(205, 126, 194, 0.12)
    border-left: 4px solid #cd7ec2
    border-radius: 10px
    padding: 12px 12px 14px
    font-size: 13px
    line-height: 1.45

.lucy-panel__preset
    display: block
    width: 100%
    text-align: left
    border: 1px solid rgba(205, 126, 194, 0.45)
    background: var(--v-surface-base)
    border-radius: 10px
    padding: 10px 12px
    margin-bottom: 8px
    font-size: 13px
    font-weight: 600
    cursor: pointer

    &:hover
        background: rgba(205, 126, 194, 0.08)
</style>
